import { z } from 'zod';
import {
  IntegrationOperationsView,
  LineageWorkbenchView,
  QueryMetadata,
  type QueryResult,
} from '@aurora-pvi/contracts';
import { apiFetch } from './apiFetch';

/**
 * Integration operations, lineage and one command, over HTTP.
 *
 * All three screens behind this read `packages/testing` fixtures until now —
 * which made the connector health dashboard report the health of connectors
 * that did not exist, and made the lineage workbench prove traceability with
 * authored data. Both are parsed against their real contracts here, so a
 * response that has drifted fails loudly rather than rendering.
 */
const OperationsEnvelope = z.object({ data: IntegrationOperationsView, meta: QueryMetadata });
const LineageEnvelope = z.object({ data: LineageWorkbenchView, meta: QueryMetadata });

/**
 * What a sync attempt did.
 *
 * `Skipped` is its own outcome and not a failure: a connection nobody has given
 * a credential to has not broken, it has not been finished, and telling an
 * administrator their feed failed would send them to debug the wrong thing.
 */
export const ConnectorSyncOutcome = z.object({
  connectorId: z.string(),
  connectorName: z.string(),
  runId: z.string().nullable(),
  status: z.enum(['Completed', 'Warning', 'Failed', 'Skipped']),
  message: z.string(),
  counts: z.object({
    submitted: z.number(),
    valid: z.number(),
    warning: z.number(),
    rejected: z.number(),
    created: z.number(),
    updated: z.number(),
    unchanged: z.number(),
  }),
});
export type ConnectorSyncOutcome = z.infer<typeof ConnectorSyncOutcome>;

const SyncEnvelope = z.object({ data: ConnectorSyncOutcome });

/** One run and the rows it rejected — the run detail screen's whole payload. */
export const IngestionRunDetail = z.object({
  data: z.object({
    run: z.object({
      id: z.string(),
      status: z.string(),
      is_dry_run: z.boolean(),
      started_at: z.string().nullable(),
      completed_at: z.string().nullable(),
      duration_ms: z.number().nullable(),
      dataset_contract_code: z.string().nullable(),
      source_system_code: z.string().nullable(),
      error_summary: z.string().nullable(),
      watermark_value: z.string().nullable(),
      submitted_count: z.number(),
      read_count: z.number(),
      valid_count: z.number(),
      warning_count: z.number(),
      rejected_count: z.number(),
      unchanged_count: z.number(),
      created_count: z.number(),
      updated_count: z.number(),
      file_name: z.string().nullable(),
      connector_name: z.string().nullable(),
      source_system_name: z.string().nullable(),
      dataset_name: z.string().nullable(),
      initiated_by: z.string().nullable(),
    }),
    exceptions: z.array(
      z.object({
        sheet_name: z.string().nullable(),
        row_number: z.number().nullable(),
        field_name: z.string().nullable(),
        submitted_value: z.string().nullable(),
        rule_code: z.string(),
        severity: z.string(),
        message: z.string(),
        remediation: z.string(),
        created_at: z.string(),
      }),
    ),
  }),
});
export type IngestionRunDetail = z.infer<typeof IngestionRunDetail>['data'];

export function createHttpIntegrationRepository(baseUrl: string) {
  return {
    async getOperations(): Promise<QueryResult<IntegrationOperationsView>> {
      const response = await apiFetch(`${baseUrl}/integrations`);
      if (!response.ok) throw new Error(`GET /integrations failed: ${response.status}`);
      return OperationsEnvelope.parse((await response.json()) as unknown);
    },

    async getWorkbench(datasetId?: string): Promise<QueryResult<LineageWorkbenchView>> {
      const query = datasetId ? `?dataset=${encodeURIComponent(datasetId)}` : '';
      const response = await apiFetch(`${baseUrl}/lineage${query}`);
      if (!response.ok) throw new Error(`GET /lineage failed: ${response.status}`);
      return LineageEnvelope.parse((await response.json()) as unknown);
    },

    /**
     * Refresh one connection now.
     *
     * A 403 is returned as a readable sentence rather than thrown as a status
     * code: the screen renders the answer in the dialog the reader pressed the
     * button in, and "403" in a toast tells them nothing they can act on.
     */
    async sync(connectorId: string): Promise<ConnectorSyncOutcome> {
      const response = await apiFetch(
        `${baseUrl}/integrations/connectors/${connectorId}/sync`,
        { method: 'POST' },
      );
      if (response.status === 403 || response.status === 404) {
        const body = (await response.json()) as { message?: string };
        throw new Error(body.message ?? 'This connection could not be refreshed.');
      }
      if (!response.ok) throw new Error(`Refreshing the connection failed: ${response.status}`);
      return SyncEnvelope.parse((await response.json()) as unknown).data;
    },

    async getRun(runId: string): Promise<IngestionRunDetail> {
      const response = await apiFetch(`${baseUrl}/import/runs/${runId}`);
      if (response.status === 404) throw new Error('NotFound');
      if (!response.ok) throw new Error(`GET /import/runs failed: ${response.status}`);
      return IngestionRunDetail.parse((await response.json()) as unknown).data;
    },
  };
}
